import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger.js';

const logger = createLogger('AdminCreditsService');

// Helper function to check if user is admin or moderator
const checkAdminAccess = async () => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  
  if (authError || !user) {
    throw new Error('Authentication required');
  }

  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (profileError || !['admin', 'moderator'].includes(profile?.role)) {
    throw new Error('Admin or moderator access required');
  }

  return user;
};

// Log admin action
const logAdminAction = async (adminId, actionType, reason, notes = null, targetUserId = null) => {
  try {
    const insertData = {
      admin_user_id: adminId,
      action_type: actionType,
      target_user_id: targetUserId,
      reason: reason,
      notes: notes
    };
    
    const { error } = await supabase
      .from('admin_actions')
      .insert(insertData);
    
    if (error) {
      logger.error('Failed to log admin action:', error);
    }
  } catch (error) {
    logger.error('Failed to log admin action', error);
  }
};

/**
 * Admin Credits Service
 * Manages AI credits for employer users
 */
export class AdminCreditsService {
  /**
   * Get all employers with their credit information
   * @param {Object} filters - Filter options
   * @param {string} filters.search - Search by name or email
   * @param {string} filters.tier - Filter by subscription tier
   * @param {number} filters.minCredits - Minimum credits filter
   * @param {number} filters.maxCredits - Maximum credits filter
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getEmployersWithCredits(filters = {}) {
    try {
      await checkAdminAccess();

      let query = supabase
        .from('user_profiles')
        .select('id, email, full_name, ai_credits_remaining, ai_credits_used, ai_generation_count, subscription_tier, created_at, updated_at')
        .eq('role', 'employer')
        .order('ai_credits_used', { ascending: false });

      // Apply filters
      if (filters.tier && filters.tier !== 'all') {
        query = query.eq('subscription_tier', filters.tier);
      }

      if (filters.minCredits !== undefined && filters.minCredits !== '') {
        query = query.gte('ai_credits_remaining', filters.minCredits);
      }

      if (filters.maxCredits !== undefined && filters.maxCredits !== '') {
        query = query.lte('ai_credits_remaining', filters.maxCredits);
      }

      if (filters.search && filters.search.trim()) {
        const searchTerm = filters.search.trim();
        query = query.or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      logger.error('getEmployersWithCredits error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get credit usage statistics across the platform
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getCreditUsageStats() {
    try {
      await checkAdminAccess();

      // Get aggregate statistics
      const { data: stats, error: statsError } = await supabase
        .from('user_profiles')
        .select('ai_credits_remaining, ai_credits_used, ai_generation_count, subscription_tier')
        .eq('role', 'employer');

      if (statsError) throw statsError;

      // Calculate statistics
      const totalCreditsAllocated = stats.reduce((sum, user) => sum + (user.ai_credits_remaining || 0), 0);
      const totalCreditsUsed = stats.reduce((sum, user) => sum + (user.ai_credits_used || 0), 0);
      const totalGenerations = stats.reduce((sum, user) => sum + (user.ai_generation_count || 0), 0);
      const averageUsage = stats.length > 0 ? totalCreditsUsed / stats.length : 0;

      // Get tier distribution
      const tierDistribution = stats.reduce((acc, user) => {
        const tier = user.subscription_tier || 'free';
        acc[tier] = (acc[tier] || 0) + 1;
        return acc;
      }, {});

      return {
        success: true,
        data: {
          totalEmployers: stats.length,
          totalCreditsAllocated,
          totalCreditsUsed,
          totalGenerations,
          averageUsage: Math.round(averageUsage * 100) / 100,
          tierDistribution
        }
      };
    } catch (error) {
      logger.error('getCreditUsageStats error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Adjust credits for a single user
   * @param {string} userId - User ID
   * @param {number} amount - Amount to add (positive) or deduct (negative)
   * @param {string} reason - Reason for adjustment
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async adjustUserCredits(userId, amount, reason) {
    try {
      const admin = await checkAdminAccess();

      // Get current credit balance
      const { data: currentUser, error: fetchError } = await supabase
        .from('user_profiles')
        .select('ai_credits_remaining, email, full_name')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      const previousCredits = currentUser.ai_credits_remaining || 0;

      // Use the add_ai_credits RPC function
      const { data, error } = await supabase
        .rpc('add_ai_credits', {
          p_user_id: userId,
          p_amount: amount
        });

      if (error) throw error;

      const result = Array.isArray(data) ? data[0] : data;
      
      if (!result.success) {
        throw new Error(result.error_message || 'Failed to adjust credits');
      }

      const newCredits = result.credits_remaining;

      // Log admin action
      await logAdminAction(
        admin.id,
        'credits_adjusted',
        reason,
        `User: ${currentUser.email} (${currentUser.full_name}). Previous: ${previousCredits}, New: ${newCredits}, Adjustment: ${amount > 0 ? '+' : ''}${amount}`,
        userId
      );

      return {
        success: true,
        data: {
          previousCredits,
          newCredits,
          adjustment: amount
        }
      };
    } catch (error) {
      logger.error('adjustUserCredits error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bulk adjust credits for multiple users
   * @param {string[]} userIds - Array of user IDs
   * @param {number} amount - Amount to add (positive) or deduct (negative)
   * @param {string} reason - Reason for adjustment
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async bulkAdjustCredits(userIds, amount, reason) {
    try {
      const admin = await checkAdminAccess();

      const results = {
        successful: [],
        failed: []
      };

      for (const userId of userIds) {
        try {
          const result = await this.adjustUserCredits(userId, amount, reason);
          if (result.success) {
            results.successful.push({ userId, ...result.data });
          } else {
            results.failed.push({ userId, error: result.error });
          }
        } catch (error) {
          results.failed.push({ userId, error: error.message });
        }
      }

      // Log bulk operation
      await logAdminAction(
        admin.id,
        'bulk_credits_adjusted',
        reason,
        `Bulk adjustment: ${userIds.length} users, ${amount > 0 ? '+' : ''}${amount} credits. Success: ${results.successful.length}, Failed: ${results.failed.length}`,
        null
      );

      return {
        success: true,
        data: results
      };
    } catch (error) {
      logger.error('bulkAdjustCredits error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get AI generation history for a specific user
   * @param {string} userId - User ID
   * @param {number} limit - Maximum number of records to return
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getUserCreditHistory(userId, limit = 50) {
    try {
      await checkAdminAccess();

      const { data, error } = await supabase
        .from('ai_generation_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      logger.error('getUserCreditHistory error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get recent credit adjustments (admin actions)
   * @param {number} limit - Maximum number of records to return
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getRecentCreditAdjustments(limit = 100) {
    try {
      await checkAdminAccess();

      const { data, error } = await supabase
        .from('admin_actions')
        .select('*, admin:user_profiles!admin_user_id(email, full_name), target:user_profiles!target_user_id(email, full_name)')
        .in('action_type', ['credits_adjusted', 'bulk_credits_adjusted'])
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      logger.error('getRecentCreditAdjustments error:', error);
      return { success: false, error: error.message };
    }
  }
}

export default AdminCreditsService;
